import { useQuery as useConnectQuery } from '@connectrpc/connect-query'
import { Heading } from '@magnetic/heading'
import { Text } from '@magnetic/text'
import { Card } from '@magnetic/card'
import { Flex } from '@magnetic/flex'
import { Numeral } from '@magnetic/numeral'
import { Container } from '@magnetic/container'
import { HealthCard } from '@magnetic/health-card'
import { Skeleton } from '@magnetic/skeleton'
import {
  ShieldCheck,
  Activity,
  Folder,
  XCircle,
  HardDrives,
  Database,
  GitBranch,
  Cube,
  Package,
  Key,
  Stack,
  Broadcast,
  Chat,
  // Magnetic Status Icons (used in System Health section)
  Positive,
  WarningStatus,
  Negative,
  Information,
} from '@/lib/icons'
import { getOperatorMetrics, getSystemHealth } from '@/lib/api'

export function AdminPage() {
  const { data: metricsResponse, isLoading: statsLoading } = useConnectQuery(
    getOperatorMetrics,
    {},
    {
      refetchInterval: 30000,
    }
  )

  const stats = metricsResponse?.metrics ? {
    totalProjects: metricsResponse.metrics.totalProjects,
    totalUsers: 0, // Not available in metrics
    totalApplications: metricsResponse.metrics.totalApplications,
  } : undefined

  const { data: health, isLoading: healthLoading } = useConnectQuery(
    getSystemHealth,
    {},
    {
      refetchInterval: 15000, // Check health more frequently
    }
  )

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <Positive hasBackground />
      case 'degraded':
        return <WarningStatus hasBackground />
      case 'unhealthy':
        return <Negative hasBackground />
      default:
        return <Information hasBackground />
    }
  }

  const getServiceIcon = (name: string) => {
    const size = 16
    const weight = 'bold' as const
    switch (name) {
      case 'mongodb':
        return <Database size={size} weight={weight} />
      case 'argocd':
        return <GitBranch size={size} weight={weight} />
      case 'kubernetes':
        return <Cube size={size} weight={weight} />
      case 'github':
        return <GitBranch size={size} weight={weight} />
      case 'ecr':
        return <Package size={size} weight={weight} />
      case 'oidc':
        return <Key size={size} weight={weight} />
      case 'opentelemetry':
        return <Broadcast size={size} weight={weight} />
      case 'prometheus':
        return <Activity size={size} weight={weight} />
      case 'webex':
        return <Chat size={size} weight={weight} />
      case 'helm':
        return <Package size={size} weight={weight} />
      case 'traefik':
        return <Stack size={size} weight={weight} />
      default:
        return <HardDrives size={size} weight={weight} />
    }
  }


  return (
    <Flex direction="vertical" gap={24}>
      {/* Page Header */}
      <Flex direction="vertical" gap={4}>
        <Flex direction="horizontal" gap={8} align="center">
          <ShieldCheck size={24} weight="bold" style={{ color: 'var(--interact-primary)' }} />
          <Heading size="h1">Admin Panel</Heading>
        </Flex>
        <Text color="light">Platform administration and system health</Text>
      </Flex>

      {/* Platform Overview - Visual Stats */}
      <Container>
        <Flex direction="vertical" gap={16}>
          <Flex direction="horizontal" gap={8} align="center">
            <Activity size={20} weight="bold" />
            <Heading size="section">Platform overview</Heading>
          </Flex>

          {statsLoading ? (
            <Card.Group>
              <Card>
                <Flex align="center" direction="horizontal" justify="space-between">
                  <Flex align="flex-end" direction="horizontal" gap={4}>
                    <Skeleton height={32} style={{ width: 60 }} />
                    <Skeleton height={16} style={{ width: 60 }} />
                  </Flex>
                  <Skeleton height={24} style={{ width: 24 }} />
                </Flex>
              </Card>
              <Card>
                <Flex align="center" direction="horizontal" justify="space-between">
                  <Flex align="flex-end" direction="horizontal" gap={4}>
                    <Skeleton height={32} style={{ width: 60 }} />
                    <Skeleton height={16} style={{ width: 80 }} />
                  </Flex>
                  <Skeleton height={24} style={{ width: 24 }} />
                </Flex>
              </Card>
            </Card.Group>
          ) : (
            <Card.Group>
              <Card>
                <Flex align="center" direction="horizontal" justify="space-between">
                  <Flex align="flex-end" direction="horizontal" gap={4}>
                    <Numeral value={String(stats?.totalProjects || 0)} size="n2" />
                    <Text size="p3" color="light">Projects</Text>
                  </Flex>
                  <Folder size={24} weight="bold" style={{ color: 'var(--interact-primary)', opacity: 0.3 }} />
                </Flex>
              </Card>

              <Card>
                <Flex align="center" direction="horizontal" justify="space-between">
                  <Flex align="flex-end" direction="horizontal" gap={4}>
                    <Numeral value={String(stats?.totalApplications || 0)} size="n2" />
                    <Text size="p3" color="light">Applications</Text>
                  </Flex>
                  <Package size={24} weight="bold" style={{ color: 'var(--interact-primary)', opacity: 0.3 }} />
                </Flex>
              </Card>
            </Card.Group>
          )}
        </Flex>
      </Container>

      {/* Application Health Summary */}
      <Container>
        <Flex direction="vertical" gap={16}>
          <Flex direction="horizontal" gap={8} align="center">
            <Stack size={20} weight="bold" />
            <Heading size="section">Application health summary</Heading>
          </Flex>

          {healthLoading ? (
            <Card.Group>
              {/* Skeleton for Health Status */}
              <Card style={{ minHeight: 140 }}>
                <Flex direction="vertical" gap={12}>
                  <Flex direction="horizontal" gap={8} align="center">
                    <Skeleton height={24} style={{ width: 24, borderRadius: '50%' }} />
                    <Skeleton height={20} style={{ width: 100 }} />
                  </Flex>
                  <Flex direction="vertical" gap={8}>
                    <Skeleton height={40} />
                    <Skeleton height={40} />
                  </Flex>
                </Flex>
              </Card>

              {/* Skeleton for Sync Status */}
              <Card style={{ minHeight: 140 }}>
                <Flex direction="vertical" gap={12}>
                  <Flex direction="horizontal" gap={8} align="center">
                    <Skeleton height={24} style={{ width: 24, borderRadius: '50%' }} />
                    <Skeleton height={20} style={{ width: 90 }} />
                  </Flex>
                  <Flex direction="vertical" gap={8}>
                    <Skeleton height={40} />
                    <Skeleton height={40} />
                  </Flex>
                </Flex>
              </Card>

              {/* Skeleton for Overview */}
              <Card style={{ minHeight: 140 }}>
                <Flex direction="vertical" gap={12}>
                  <Skeleton height={20} style={{ width: 80 }} shade="dark" />
                  <Flex direction="horizontal" gap={36}>
                    <Flex direction="vertical" align="center" gap={4}>
                      <Skeleton height={32} style={{ width: 40 }} />
                      <Skeleton height={14} style={{ width: 60 }} />
                    </Flex>
                    <Flex direction="vertical" align="center" gap={4}>
                      <Skeleton height={24} style={{ width: 50 }} />
                      <Skeleton height={14} style={{ width: 50 }} />
                    </Flex>
                    <Flex direction="vertical" align="center" gap={4}>
                      <Skeleton height={24} style={{ width: 50 }} />
                      <Skeleton height={14} style={{ width: 50 }} />
                    </Flex>
                  </Flex>
                </Flex>
              </Card>
            </Card.Group>
          ) : health && health.applications ? (
            <HealthCard.Group>
              {/* Health Status - Use HealthCard */}
              <HealthCard
                title="Health Status"
                icon={<Activity size={24} weight="bold" />}
                clusters={{
                  online: health.applications.healthy || 0,
                  warning: (health.applications.degraded || 0) + (health.applications.progressing || 0),
                  offline: health.applications.missing || 0,
                }}
              />

              {/* Sync Status - Use HealthCard */}
              <HealthCard
                title="Sync Status"
                icon={<GitBranch size={24} weight="bold" />}
                clusters={{
                  online: health.applications.synced || 0,
                  warning: health.applications.outOfSync || 0,
                  offline: health.applications.syncFailed24h || 0,
                }}
              />

              {/* Overview Card - Keep custom */}
              <Card>
                <Flex direction="vertical" gap={12}>
                  <Heading size="sub-section">Overview</Heading>
                  <Flex direction="horizontal" gap={36}>
                    <Flex direction="vertical" align="center" gap={4}>
                      <Numeral value={String(health.applications.total || 0)} size="n1" />
                      <Text size="p4" color="light">Total apps</Text>
                    </Flex>
                    <Flex direction="vertical" align="center" gap={4}>
                      <Text size="p1" weight="bold" style={{ color: 'var(--status-positive)' }}>
                        {(health.applications.total || 0) > 0
                          ? Math.round(((health.applications.healthy || 0) / health.applications.total) * 100)
                          : 0}%
                      </Text>
                      <Text size="p4" color="light">Healthy</Text>
                    </Flex>
                    <Flex direction="vertical" align="center" gap={4}>
                      <Text size="p1" weight="bold" style={{ color: 'var(--status-positive)' }}>
                        {(health.applications.total || 0) > 0
                          ? Math.round(((health.applications.synced || 0) / health.applications.total) * 100)
                          : 0}%
                      </Text>
                      <Text size="p4" color="light">Synced</Text>
                    </Flex>
                  </Flex>
                </Flex>
              </Card>
            </HealthCard.Group>
          ) : null}
        </Flex>
      </Container>

      {/* System Health Status */}
      <Container>
        <Flex direction="vertical" gap={16}>
          <Flex direction="horizontal" justify="space-between" align="center">
            <Flex direction="horizontal" gap={8} align="center">
              <HardDrives size={20} weight="bold" />
              <Heading size="section">System health</Heading>
            </Flex>
            {health && !healthLoading && (
              <Flex direction="horizontal" gap={8} align="center">
                {getStatusIcon(health.overallStatus)}
                <Text size="p3" style={{ textTransform: 'capitalize' }}>{health.overallStatus}</Text>
              </Flex>
            )}
            {healthLoading && (
              <Skeleton height={24} style={{ width: 100 }} />
            )}
          </Flex>

          {healthLoading ? (
            <Card.Group>
              {/* Skeleton for multiple service cards */}
              {[...Array(6)].map((_, i) => (
                <Card key={i} style={{ minHeight: 120 }}>
                  <Flex direction="vertical" gap={8}>
                    <Flex direction="horizontal" justify="space-between" align="center">
                      <Flex direction="horizontal" gap={8} align="center">
                        <Skeleton height={16} style={{ width: 16 }} />
                        <Skeleton height={18} style={{ width: 100 }} />
                      </Flex>
                      <Skeleton height={24} style={{ width: 24, borderRadius: '50%' }} />
                    </Flex>
                    <Skeleton height="sm" style={{ width: '90%' }} />
                    <Skeleton height="sm" style={{ width: '70%' }} />
                  </Flex>
                </Card>
              ))}
            </Card.Group>
          ) : !health ? (
            <Flex direction="vertical" align="center" gap={8} style={{ padding: 32 }}>
              <XCircle size={32} weight="bold" style={{ color: 'var(--status-negative)' }} />
              <Text size="p3" color="light">Failed to load system health</Text>
            </Flex>
          ) : (
            <Card.Group>
              {/* Service Health Cards */}
              {health.services.map((service) => (
                <Card key={service.name}>
                  <Flex direction="vertical" gap={8}>
                    <Flex direction="horizontal" justify="space-between" align="center">
                      <Flex direction="horizontal" gap={8} align="center">
                        {getServiceIcon(service.name)}
                        <Text size="p3" weight="semi-bold" style={{ textTransform: 'capitalize' }}>{service.name}</Text>
                      </Flex>
                      {getStatusIcon(service.status)}
                    </Flex>
                    <Text size="p4" color="light" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {service.message}
                    </Text>
                    {service.details && Object.keys(service.details).length > 0 && (
                      <Flex direction="vertical" gap={4}>
                        {Object.entries(service.details).map(([key, value]) => (
                          <Flex key={key} direction="horizontal" justify="space-between">
                            <Text size="p4" color="light" style={{ textTransform: 'capitalize' }}>{key.replace(/_/g, ' ')}:</Text>
                            <Text size="p4" monospace>{value}</Text>
                          </Flex>
                        ))}
                      </Flex>
                    )}
                  </Flex>
                </Card>
              ))}

              {/* Controller Health Card */}
              {health.controller && (
                <Card>
                  <Flex direction="vertical" gap={8}>
                    <Flex direction="horizontal" justify="space-between" align="center">
                      <Flex direction="horizontal" gap={8} align="center">
                        <Activity size={16} weight="bold" />
                        <Text size="p3" weight="semi-bold">Controller</Text>
                      </Flex>
                      {getStatusIcon(health.controller.status)}
                    </Flex>
                    <Text size="p4" color="light">
                      {health.controller.running ? 'Running' : 'Not running'}
                    </Text>
                    <Flex direction="vertical" gap={4}>
                      <Flex direction="horizontal" justify="space-between">
                        <Text size="p4" color="light">Watched Apps:</Text>
                        <Text size="p4" monospace>{health.controller.watchedApplications}</Text>
                      </Flex>
                      {(health.controller.reconcileErrors24h ?? 0) > 0 && (
                        <Flex direction="horizontal" justify="space-between">
                          <Text size="p4" style={{ color: 'var(--status-warning)' }}>Errors (24h):</Text>
                          <Text size="p4" monospace style={{ color: 'var(--status-warning)' }}>{health.controller.reconcileErrors24h}</Text>
                        </Flex>
                      )}
                    </Flex>
                  </Flex>
                </Card>
              )}
            </Card.Group>
          )}
        </Flex>
      </Container>
    </Flex>
  )
}
